import { Component, Inject, OnInit, OnDestroy, Injector, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Parameter, ParameterThreshold } from 'src/app/models/parameter.model';
import { ParameterService } from 'src/app/services/parameter.service';
import { FormComponent, IFormDialogData } from 'src/app/shared/form/form.component';
import { ParameterFieldEnum, ParameterTypeEnum } from 'src/app/enums/parameterType.enum';
import { getSelectableSampleTypes } from 'src/app/enums/sampleType.enum';
import { AveragingTypeEnum } from 'src/app/enums/averagingType.enum';
import { MeasurementModeEnum } from 'src/app/enums/measurementMode.enum';
import { getEnumLookup } from 'src/app/_helpers/enum-helper';
import { ParameterUnitService } from 'src/app/services/parameter-unit.service';
import { OrganizationLevel } from 'src/app/models/organizationLevel.model';
import { toOrganizationTree } from 'src/app/_helpers/org-tree-helper';
import { catchError, map, switchMap, takeUntil } from 'rxjs/operators';
import { forkJoin, of, Subject } from 'rxjs';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { ChartService } from 'src/app/services/chart.service';
import { ParameterThresholdEditorComponent } from 'src/app/common-components/parameter-threshold-editor/parameter-threshold-editor.component';

interface SelectOption<T = number | string> {
  id: T;
  name: string;
}

export interface ParameterDialogData extends IFormDialogData {
  organizationLevelId?: string;
}

@Component({
  standalone: false,
  selector: 'app-parameter-dialog',
  templateUrl: './parameter-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./parameter-dialog.component.scss']
})
export class ParameterDialogComponent extends FormComponent<Parameter> implements OnInit, OnDestroy {
  @ViewChild(ParameterThresholdEditorComponent) private thresholdEditor?: ParameterThresholdEditorComponent;

  private readonly destroy$ = new Subject<void>();

  form: FormGroup;
  thresholds: ParameterThreshold[] = [];
  currentUnit = '—';
  isSaving = false;
  readonly averagingTypeEnum = AveragingTypeEnum;

  parameterTypeOptions: SelectOption[] = [];
  sampleTypeOptions: SelectOption[] = [];
  averagingTypeOptions: SelectOption[] = [];
  measurementModeOptions: SelectOption[] = [];
  orgLevelOptions: OrganizationLevel[] = [];

  private readonly unitOverrides: Partial<Record<ParameterTypeEnum, string>> = {
    [ParameterTypeEnum.Latitude]: '°',
    [ParameterTypeEnum.Longitude]: '°',
    [ParameterTypeEnum.Lavg]: 'dB',
    [ParameterTypeEnum.BinMass]: 'mg',
    [ParameterTypeEnum.BatteryVoltage]: 'V',
    [ParameterTypeEnum.ChargingVoltage]: 'V',
    [ParameterTypeEnum.ChargingCurrent]: 'mA',
    [ParameterTypeEnum.SystemCurrent]: 'mA',
    [ParameterTypeEnum.SignalStrength]: 'dBm',
    [ParameterTypeEnum.PowerState]: 'state',
    [ParameterTypeEnum.BatteryCharging]: 'bool',
    [ParameterTypeEnum.Orientation]: 'g',
    [ParameterTypeEnum.WindDirection]: '°',
    [ParameterTypeEnum.Count0_3]: 'particles/cm³',
    [ParameterTypeEnum.Count0_5]: 'particles/cm³',
    [ParameterTypeEnum.Count1_0]: 'particles/cm³',
    [ParameterTypeEnum.Count2_5]: 'particles/cm³',
    [ParameterTypeEnum.Count5_0]: 'particles/cm³',
    [ParameterTypeEnum.Count10]: 'particles/cm³',
    [ParameterTypeEnum.Pressure]: 'hPa',
    [ParameterTypeEnum.Rainfall]: 'mm',
    [ParameterTypeEnum.MeasuredRCS]: 'Ω'
  };

  constructor(
    public dialogRef: MatDialogRef<ParameterDialogComponent>,
    private parameterService: ParameterService,
    @Inject(MAT_DIALOG_DATA) public dialogData: ParameterDialogData,
    protected injector: Injector,
    private parameterUnitService: ParameterUnitService,
    private fb: FormBuilder,
    private chartService: ChartService
  ) {
    super(dialogRef, parameterService, dialogData, 'Parameter', injector);
    this.form = this.initForm();
  }

  ngOnInit(): void {
    this.initOptions();
    forkJoin({
      organizationLevels: this.parameterService.getOrganizationLevels().pipe(
        catchError(() => {
          this.orgLevelOptions = [];
          this.form.get(nameOf<Parameter>(x => x.organizationLevelId))?.reset();
          this.toastService.displayErrorMessage('Failed to load lookup data', true);
          return of([] as OrganizationLevel[]);
        })
      ),
      parameter: this.getEntity()
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ organizationLevels, parameter }) => {
          this.orgLevelOptions = toOrganizationTree(
            organizationLevels
              .map(level => ({
                id: level.id,
                name: level.name,
                parentId: level.parentId,
                code: level.code,
                orgLevelType: level.orgLevelType,
                children: []
              }))
              .filter(level => !!level.id) as OrganizationLevel[]
          );

          this.entity = parameter;
          this.setSampleTypeOptions();
          this.populateForm();

          // The parameter (including its thresholds) is already loaded via getEntity();
          // the threshold editor loads/owns its own bands, so no second fetch is needed.
          this.currentUnit = this.getUnitForParameterType(this.entity?.parameterType);
          if (this.entity?.thresholds && Array.isArray(this.entity.thresholds)) {
            this.thresholds = [...this.entity.thresholds].sort(
              (a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)
            );
          }
          this.updateFieldVisibility();
        },
        error: (_error) => {
          this.toastService.displayErrorMessage('Failed to load parameter', true);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getNewObject(): Parameter {
    const param = new Parameter();

    if (this.dialogData?.organizationLevelId != null) {
      param.organizationLevelId = this.dialogData.organizationLevelId;
    }

    return param;
  }

  validate(): boolean {
    if (!this.orgLevelOptions.length) {
      this.toastService.displayErrorMessage('Failed to load lookup data', true);
      return false;
    }

    if (!this.form.valid) {
      this.toastService.displayErrorMessage('Please fix validation errors before saving', true);
      return false;
    }
    return true;
  }

  private initForm(): FormGroup {
    return this.fb.group({
      id: [null],
      analyteName: ['', Validators.required],
      parameterType: ['', Validators.required],
      type: ['', Validators.required],
      measurementMode: [MeasurementModeEnum.Derived, Validators.required],
      averagingType: [AveragingTypeEnum.None, Validators.required],
      averagingTimeSeconds: [null],
      isNotificationOnly: [false],
      organizationLevelId: [null, Validators.required],
      healthAdvisory: [''],
      specificRecommendations: ['']
    });
  }

  private populateForm(): void {
    this.form.patchValue({
      id: this.entity?.id,
      analyteName: this.entity?.analyteName || '',
      parameterType: this.entity?.parameterType,
      type: this.entity?.type,
      measurementMode: this.entity?.measurementMode ?? MeasurementModeEnum.Derived,
      averagingType: this.entity?.averagingType ?? AveragingTypeEnum.None,
      averagingTimeSeconds: this.entity?.averagingType !== AveragingTypeEnum.None && this.entity?.averagingTimeSeconds != null
        ? this.entity.averagingTimeSeconds / 60
        : null,
      isNotificationOnly: this.entity?.isNotificationOnly || false,
      organizationLevelId: this.entity?.organizationLevelId,
      healthAdvisory: this.entity?.healthAdvisory || '',
      specificRecommendations: this.entity?.specificRecommendations || ''
    });

    this.onAveragingTypeChange();
  }

  get model(): Parameter {
    return this.entity;
  }

  get readonly(): boolean {
    return this.isReadonly;
  }

  onCancel(): void {
    this.close();
  }

  private initOptions(): void {
    this.parameterTypeOptions = Object.entries(ParameterFieldEnum)
      .filter(([key]) => isNaN(Number(key))) // Keep only string keys
      .map(([key, value]) => {
        const parameterTypeId = Number(value);
        const parameterTypeName = Number.isFinite(parameterTypeId)
          ? this.chartService.getParameterName(parameterTypeId as ParameterTypeEnum)
          : null;

        return {
          id: parameterTypeId,
          name: parameterTypeName || this.formatFieldName(key)
        };
      })
      .filter((option) => Number.isFinite(option.id))
      .sort((a, b) => a.name.localeCompare(b.name));

    this.setSampleTypeOptions();

    this.averagingTypeOptions = getEnumLookup(AveragingTypeEnum)
      .sort((a, b) => a.id - b.id);

    this.measurementModeOptions = [
      { id: MeasurementModeEnum.Direct, name: this.translateService.getDynamicTranslation('Direct') },
      { id: MeasurementModeEnum.Derived, name: this.translateService.getDynamicTranslation('Derived') }
    ];

    this.orgLevelOptions = [];
  }

  // Gate agent-type-only classifications (Other, Vibration) out of the analyte sample-type
  // dropdown — they cannot have samples, so an analyte cannot be measured against them yet. The
  // current parameter's type is preserved on edit so an existing value never renders blank; this is
  // re-run after the entity loads because initOptions runs before the parameter is fetched.
  private setSampleTypeOptions(): void {
    this.sampleTypeOptions = getSelectableSampleTypes(this.entity?.type ?? null)
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  onOrganizationLevelSelectionChange(selectedId: string | null): void {
    if (this.readonly) {
      return;
    }

    this.form.get(nameOf<Parameter>(x => x.organizationLevelId))?.setValue(selectedId);
    this.form.get(nameOf<Parameter>(x => x.organizationLevelId))?.markAsTouched();
    this.form.get(nameOf<Parameter>(x => x.organizationLevelId))?.markAsDirty();
  }

  private formatFieldName(key: string): string {
    let formatted = key.replace(/_/g, ' ');
    formatted = formatted.replace(/([a-z])([A-Z])/g, '$1 $2');
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  }

  onAveragingTypeChange(): void {
    const control = this.form.get(nameOf<Parameter>(x => x.averagingTimeSeconds));
    if (this.form.get(nameOf<Parameter>(x => x.averagingType))?.value === AveragingTypeEnum.None) {
      control?.clearValidators();
    } else {
      control?.setValidators([Validators.required, Validators.min(0)]);
    }
    control?.updateValueAndValidity();
  }

  onParameterTypeChange(): void {
    this.currentUnit = this.getUnitForParameterType(this.form.get(nameOf<Parameter>(x => x.parameterType))?.value);
  }

  getUnitForParameterType(paramType: number | string | null | undefined): string {
    if (!paramType && paramType !== 0) {
        return '—';
    }

    const parameterType = typeof paramType === 'string'
      ? parseInt(paramType, 10)
      : paramType;

    if (Number.isNaN(parameterType)) {
      return '—';
    }

    return this.unitOverrides[parameterType as ParameterTypeEnum]
      ?? (this.parameterUnitService.getParameterUnit(parameterType as ParameterTypeEnum) || '—');
  }

  getFieldNameForParameterType(paramType: number | string | null | undefined): string {
    if (!paramType && paramType !== 0) {
        return '';
    }

    const fieldKey = Object.entries(ParameterFieldEnum)
      .find(([key, value]) => isNaN(Number(key)) && value === paramType)?.[0];

    return fieldKey || '';
  }


  // Display/state-only sync: keeps the parent entity's in-memory bands current after
  // the editor emits (saved). This is NOT part of the save flow — the unified save is
  // orchestrated in save() via persistThresholds(), which owns the threshold API call.
  onThresholdsSaved(thresholds: ParameterThreshold[]): void {
    this.thresholds = thresholds;
    this.entity.thresholds = thresholds;
  }

  override save(): void {
    if (!this.validate()) {
      return;
    }

    const editor = this.thresholdEditor;
    const hasThresholds = !!editor && editor.hasBands;

    // The API requires at least one threshold band and has no clear-all path:
    // BulkSaveThresholds rejects an empty payload ("At least one threshold band is
    // required."). If an existing parameter's bands have all been removed we must
    // block the save with that message rather than silently skipping the threshold
    // call and leaving the now-orphaned bands in the database.
    if (!hasThresholds && editor?.hadPersistedBands) {
      this.toastService.displayErrorMessage('At least one threshold band is required.', true);
      return;
    }

    if (hasThresholds && !editor!.validateBands()) {
      this.toastService.displayErrorMessage('Please fix validation errors before saving', true);
      return;
    }

    this.isSaving = true;
    this.isLoading = true;

    this.saveAction(this.entity)
      .pipe(
        switchMap((savedParameter: Parameter | Object) => {
          const parameterId = (savedParameter as Parameter)?.id ?? (this.form.value.id as string);
          this.entity.id = parameterId;
          // Sync the form's id control so a retry after a threshold-save failure
          // routes saveAction() to update() (which reads form.value.id), not create() again.
          this.form.patchValue({ [nameOf<Parameter>(x => x.id)]: parameterId });

          if (!hasThresholds) {
            return of({ parameterId, thresholdError: null as unknown });
          }

          return editor!.persistThresholds(parameterId).pipe(
            map(() => ({ parameterId, thresholdError: null as unknown })),
            catchError((thresholdError: unknown) => of({ parameterId, thresholdError }))
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (result) => {
          this.isSaving = false;
          this.isLoading = false;

          if (result.thresholdError) {
            // Parameter persisted but thresholds failed — keep the dialog open so the
            // in-memory threshold changes are retained and the user can retry.
            this.isAdd = false;
            this.toastService.displayErrorMessage('Parameter saved, but threshold bands could not be saved. Please retry.', true);
            return;
          }

          this.toastService.showSuccess('Success', true);
          this.closeDialog(this.entity);
        },
        error: (error: unknown) => {
          this.isSaving = false;
          this.isLoading = false;
          this.toastService.showError(error);
        }
      });
  }

  override saveAction(model: Parameter) {
    const formValue = this.form.value;

    if (formValue.averagingType === AveragingTypeEnum.None) {
      // Nullify stale value — field is hidden by CSS but retains its value
      formValue.averagingTimeSeconds = null;
    } else if (formValue.averagingTimeSeconds != null) {
      formValue.averagingTimeSeconds = formValue.averagingTimeSeconds * 60;
    }

    return formValue.id
      ? this.parameterService.update(formValue)
      : this.parameterService.create(formValue);
  }
}
